import { db } from '../firebase';
import { collection, query, where, getDocs, orderBy, limit, doc, setDoc } from 'firebase/firestore';

/**
 * Initialize user's quiz data
 * @param {string} userId - The user's ID
 */
const initializeUserData = async (userId) => {
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, {
        totalQuizzes: 0,
        averageScore: 0,
        bestScore: 0,
        totalXP: 0,
        streak: 0,
        lastActivityDate: null,
        createdAt: new Date()
    }, { merge: true });
};

/**
 * Gets user's quiz statistics
 * @param {string} userId - The user's ID
 * @returns {Promise<Object>} - User's quiz statistics
 */
export const getUserStats = async (userId) => {
    try {
        const attemptsRef = collection(db, 'quizAttempts');
        const userAttemptsQuery = query(
            attemptsRef,
            where('userId', '==', userId)
        );

        const querySnapshot = await getDocs(userAttemptsQuery);
        const attempts = querySnapshot.docs.map(doc => doc.data());

        // If no attempts found, initialize user data
        if (attempts.length === 0) {
            await initializeUserData(userId);
            return {
                averageScore: 0,
                totalQuizzes: 0,
                bestScore: 0
            };
        }

        const scores = attempts.map(attempt => attempt.score);
        const averageScore = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
        const bestScore = Math.max(...scores);

        return {
            averageScore,
            totalQuizzes: attempts.length,
            bestScore
        };
    } catch (error) {
        console.error('Error fetching user stats:', error);
        throw new Error('Failed to fetch user statistics. Please try again.');
    }
};

/**
 * Gets user's recent quiz attempts
 * @param {string} userId - The user's ID
 * @param {number} attemptLimit - Number of attempts to retrieve
 * @returns {Promise<Array>} - Recent quiz attempts
 */
export const getRecentQuizAttempts = async (userId, attemptLimit = 3) => {
    try {
        const attemptsRef = collection(db, 'quizAttempts');
        const recentAttemptsQuery = query(
            attemptsRef,
            where('userId', '==', userId),
            orderBy('timestamp', 'desc'),
            limit(attemptLimit)
        );

        const querySnapshot = await getDocs(recentAttemptsQuery);
        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Error fetching recent attempts:', error);
        return []; // Return empty array instead of throwing error
    }
}; 